import { useEffect, useState } from "react";
import { Loader2, ChefHat, Send, ExternalLink, LogOut, Copy, Check, Trash2, RefreshCw, Mail, ArrowRight, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, adminOrders, adminDeliver, adminDeleteOrder, adminSyncOrder, adminSendRecovery, adminRunRecoverySweep, adminMarkDelivered, adminMoveToPending } from "../lib/api";

export default function Admin() {
    // Note on auth: this is a single-admin operator panel with a strong shared password.
    // sessionStorage is used so the password isn't persisted to disk and is cleared on tab close.
    // We deliberately don't use httpOnly cookies because that would require switching from the
    // current X-Admin-Password header scheme to cookie + CSRF token plumbing, which adds
    // significant complexity for no real threat-model benefit at the current scale.
    const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
    const [password, setPassword] = useState("");
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [linkInputs, setLinkInputs] = useState({});
    const [copiedId, setCopiedId] = useState("");

    const load = async (t) => {
        setLoading(true);
        try {
            const res = await adminOrders(t);
            setOrders(res.items || []);
        } catch (e) {
            if (e?.response?.status === 401) {
                sessionStorage.removeItem("admin_token");
                setToken("");
                toast.error("Session expired");
            } else {
                toast.error("Failed to load orders");
            }
        } finally {
            setLoading(false);
        }
    };

    // `load` is intentionally not in deps — it's recreated each render and adding it
    // would cause an infinite re-fetch loop. Re-fetch only when token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (token) load(token); }, [token]);

    const onLogin = async (e) => {
        e.preventDefault();
        try {
            const r = await adminLogin(password);
            sessionStorage.setItem("admin_token", r.token);
            setToken(r.token);
            toast.success("Welcome");
        } catch {
            toast.error("Wrong password");
        }
    };

    const onDeliver = async (id) => {
        const link = (linkInputs[id] || "").trim();
        if (!link.startsWith("http")) { toast.error("Paste a valid GPT link"); return; }
        try {
            await adminDeliver(token, { submission_id: id, gpt_link: link });
            toast.success("Delivered + email sent ✓");
            await load(token);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to deliver");
        }
    };

    const onDelete = async (o) => {
        const ok = window.confirm(`Delete order from ${o.full_name} (${o.email})?\n\nThis cannot be undone.`);
        if (!ok) return;
        try {
            await adminDeleteOrder(token, o.id);
            toast.success("Order deleted");
            await load(token);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to delete");
        }
    };

    const onSync = async (o) => {
        try {
            const r = await adminSyncOrder(token, o.id);
            if (r.submission_status === "paid") {
                toast.success("Synced — payment confirmed, emails sent ✓");
            } else if (r.stripe_payment_status === "unpaid") {
                toast.message("Stripe says unpaid — customer hasn't completed checkout");
            } else {
                toast.message(`Stripe status: ${r.stripe_status}/${r.stripe_payment_status}`);
            }
            await load(token);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to sync");
        }
    };

    const onRecover = async (o) => {
        const confirmed = window.confirm(`Send abandoned-cart recovery email to ${o.full_name}?\n${o.email}`);
        if (!confirmed) return;
        try {
            const r = await adminSendRecovery(token, o.id);
            if (r.sent) toast.success("Recovery email sent ✓");
            else toast.error("Send failed (check Resend logs)");
            await load(token);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to send recovery");
        }
    };

    const onSweep = async () => {
        try {
            const r = await adminRunRecoverySweep(token);
            toast.success(`Sweep done — sent ${r.sent}, skipped ${r.skipped}`);
            await load(token);
        } catch {
            toast.error("Sweep failed");
        }
    };

    const onMarkDelivered = async (o) => {
        const ok = window.confirm(
            `Move ${o.full_name} to Delivered?\n\n` +
            `This will NOT send the delivery email — use this only when you've already shared the GPT with the customer outside the app.\n\n` +
            `If you want the customer to receive the delivery email, paste the GPT link and click Send instead.`
        );
        if (!ok) return;
        try {
            await adminMarkDelivered(token, o.id);
            toast.success("Moved to Delivered ✓");
            await load(token);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to move");
        }
    };

    const onMoveToPending = async (o) => {
        const ok = window.confirm(`Move ${o.full_name} back to Pending Delivery?`);
        if (!ok) return;
        try {
            await adminMoveToPending(token, o.id);
            toast.success("Moved back to Pending");
            await load(token);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to move");
        }
    };

    const buildPrompt = (o) => {
        const location = [o.city, o.state].filter(Boolean).join(", ");
        const arr = (v) => Array.isArray(v) ? v.join(", ") : (v || "—");
        return `Build a personalized "Cooking Assistant" Custom GPT.

Persona name: ${o.assistant_name || `${o.full_name.split(" ")[0]}'s Cooking Assistant`}

User profile:
- Name: ${o.full_name}
- Location: ${location || "—"}
- Cooks for: ${arr(o.household)}
- Diet: ${o.meal_preference}
- Specific preferences: ${arr(o.preferences)}
- Meal style: ${o.cuisine_style}
- Cooking time available: ${o.cooking_time}
- What's at home: ${arr(o.ingredients)}
- Notes / specifics: ${o.avoid_ingredients || "—"}
- Biggest challenges: ${arr(o.biggest_struggle)}

Behavior:
- This is a DECISION ENGINE, not a recipe app. Always give a clear answer first ("Tonight: X"), then offer details only if asked.
- Suggest meals that fit the diet, time window, and preferences listed above.
- Use ingredients commonly available in ${location || "their region"} — favor seasonal, local produce.
- Always offer 1 quick option and 1 indulgent option when the user is unsure.
- Tone: warm, calm, like a wise Indian aunty / mom. No fluff. Short lines.
- Address the user as ${o.full_name.split(" ")[0]}.
- When asked "what should I cook tonight?" propose 2–3 ideas with rough time and key ingredients.
- Honor any "no onion / no garlic", Jain, vegan, or sattvic constraints strictly.`;
    };

    const copyPrompt = (o) => {
        navigator.clipboard.writeText(buildPrompt(o));
        setCopiedId(o.id);
        toast.success("Prompt copied — paste in ChatGPT");
        setTimeout(() => setCopiedId(""), 1500);
    };

    const logout = () => {
        sessionStorage.removeItem("admin_token");
        setToken("");
        setOrders([]);
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-bone flex items-center justify-center px-5" data-testid="admin-login">
                <form onSubmit={onLogin} className="bg-white rounded-2xl border-2 border-ink p-8 w-full max-w-sm" style={{ boxShadow: '6px 6px 0 #1A1A1A' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-saffron-dark text-lg leading-none">✺</span>
                        <span className="font-heading text-lg font-semibold">Admin</span>
                    </div>
                    <p className="text-sm text-muted2 mb-5">Enter password to manage orders.</p>
                    <input
                        type="password"
                        className="input-field"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Password"
                        autoFocus
                        data-testid="admin-password-input"
                    />
                    <button type="submit" className="btn-saffron w-full justify-center mt-4" data-testid="admin-login-btn">Sign in</button>
                </form>
            </div>
        );
    }

    const pending = orders.filter(o => o.status === "paid");
    const delivered = orders.filter(o => o.status === "delivered");
    const incomplete = orders.filter(o => o.status !== "paid" && o.status !== "delivered");

    return (
        <div className="min-h-screen bg-bone pb-20" data-testid="admin-page">
            <div className="bg-ink text-bone">
                <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-saffron text-lg leading-none">✺</span>
                        <span className="font-heading text-lg font-semibold">Admin · My Cooking <em className="text-saffron not-italic">Assistant</em></span>
                    </div>
                    <button onClick={logout} className="text-sm text-bone/70 hover:text-saffron flex items-center gap-1.5" data-testid="admin-logout">
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-10">
                <div className="flex items-end justify-between gap-3 flex-wrap">
                    <h1 className="font-heading text-4xl">Orders</h1>
                    <button
                        onClick={onSweep}
                        className="btn-secondary text-xs"
                        title={`Send recovery emails to abandoned carts older than the configured cooldown`}
                        data-testid="run-sweep-btn"
                    >
                        <Mail className="w-3.5 h-3.5 inline mr-1" /> Run recovery sweep
                    </button>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 text-sm">
                    <Stat label="Pending delivery" value={pending.length} accent />
                    <Stat label="Delivered" value={delivered.length} />
                    <Stat label="Incomplete" value={incomplete.length} />
                </div>

                {loading && <Loader2 className="w-5 h-5 animate-spin mt-8" />}

                {!loading && (
                    <>
                        <Section title={`Pending delivery (${pending.length})`}>
                            {pending.length === 0 ? <Empty msg="Nothing to deliver right now." /> :
                                pending.map(o => (
                                    <OrderCard key={o.id} o={o} input={linkInputs[o.id] || ""} setInput={v => setLinkInputs(s => ({ ...s, [o.id]: v }))} onDeliver={onDeliver} onCopy={copyPrompt} onDelete={onDelete} onMarkDelivered={onMarkDelivered} copied={copiedId === o.id} />
                                ))}
                        </Section>

                        <Section title={`Delivered (${delivered.length})`}>
                            {delivered.length === 0 ? <Empty msg="No deliveries yet." /> :
                                delivered.map(o => <OrderCard key={o.id} o={o} delivered onCopy={copyPrompt} onDelete={onDelete} onMoveToPending={onMoveToPending} copied={copiedId === o.id} />)}
                        </Section>

                        <Section title={`Incomplete / abandoned (${incomplete.length})`}>
                            {incomplete.length === 0 ? <Empty msg="None." /> :
                                incomplete.map(o => <OrderCard key={o.id} o={o} compact onCopy={copyPrompt} onDelete={onDelete} onSync={onSync} onRecover={onRecover} copied={copiedId === o.id} />)}
                        </Section>
                    </>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <span className={`px-3 py-1.5 rounded-full border-2 border-ink ${accent ? "bg-saffron text-ink" : "bg-white text-ink"}`}>
            {label}: <strong>{value}</strong>
        </span>
    );
}

function Section({ title, children }) {
    return (
        <div className="mt-10">
            <h2 className="text-xs uppercase tracking-[0.22em] text-muted2 mb-4">{title}</h2>
            <div className="space-y-4">{children}</div>
        </div>
    );
}

function Empty({ msg }) {
    return <p className="text-muted2 text-sm bg-white border-2 border-ink rounded-2xl px-5 py-4">{msg}</p>;
}

function OrderCard({ o, input, setInput, onDeliver, delivered, compact, onCopy, copied, onDelete, onSync, onRecover, onMarkDelivered, onMoveToPending }) {
    const location = [o.city, o.state].filter(Boolean).join(", ");
    const recoverySent = !!o.recovery_email_sent_at;
    return (
        <div className="bg-white rounded-2xl border-2 border-ink p-5" data-testid={`order-card-${o.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="font-medium">{o.full_name} <span className="text-muted2 font-normal">· {o.email}</span></p>
                    <p className="text-xs text-muted2 mt-0.5">
                        Status: <strong>{o.status}</strong> · {o.package_label || "—"} · {new Date(o.created_at).toLocaleString()}
                    </p>
                    {o.assistant_name && <p className="text-sm mt-1">Wants it called: <em>"{o.assistant_name}"</em></p>}
                    {compact && recoverySent && (
                        <p className="text-[11px] uppercase tracking-[0.18em] text-saffron-dark mt-1.5">
                            <Mail className="w-3 h-3 inline mr-1 -mt-0.5" />
                            Recovery sent · {new Date(o.recovery_email_sent_at).toLocaleString()}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {o.photo_url && <img src={o.photo_url} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-ink" />}
                    {onDelete && (
                        <button
                            onClick={() => onDelete(o)}
                            title="Delete order"
                            className="w-9 h-9 inline-flex items-center justify-center rounded-full border-2 border-ink bg-white hover:bg-red-50 text-ink hover:text-red-600 transition-colors"
                            data-testid={`delete-order-${o.id}`}
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {!compact && (
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm">
                    <Field label="Diet" value={o.meal_preference} />
                    <Field label="Time" value={o.cooking_time} />
                    <Field label="Cooks for" value={o.household} />
                    <Field label="Style" value={o.cuisine_style} />
                    <Field label="Preferences" value={o.preferences} full />
                    <Field label="At home" value={o.ingredients} full />
                    <Field label="Location" value={location} />
                    <Field label="Phone" value={o.phone} />
                    <Field label="Notes" value={o.avoid_ingredients} full />
                    <Field label="Biggest challenges" value={o.biggest_struggle} full />
                </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
                <button onClick={() => onCopy(o)} className="btn-secondary text-xs" data-testid={`copy-prompt-${o.id}`}>
                    {copied ? <><Check className="w-3.5 h-3.5 inline mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 inline mr-1" /> Copy GPT prompt</>}
                </button>
                <a href="https://chatgpt.com/gpts/editor" target="_blank" rel="noreferrer" className="btn-secondary text-xs">
                    <ExternalLink className="w-3.5 h-3.5 inline mr-1" /> Open ChatGPT GPT builder
                </a>
                {compact && onSync && o.stripe_session_id && (
                    <button
                        onClick={() => onSync(o)}
                        className="btn-secondary text-xs"
                        title="Re-check this checkout in Stripe and reconcile if paid"
                        data-testid={`sync-order-${o.id}`}
                    >
                        <RefreshCw className="w-3.5 h-3.5 inline mr-1" /> Sync from Stripe
                    </button>
                )}
                {compact && onRecover && (
                    <button
                        onClick={() => onRecover(o)}
                        className="btn-secondary text-xs"
                        title="Send (or re-send) the abandoned-cart recovery email now"
                        data-testid={`recover-order-${o.id}`}
                    >
                        <Mail className="w-3.5 h-3.5 inline mr-1" /> {recoverySent ? "Resend recovery" : "Send recovery email"}
                    </button>
                )}
            </div>

            {!delivered && !compact && (
                <div className="mt-4 space-y-2">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <input
                            className="input-field flex-1 text-sm"
                            placeholder="Paste GPT share link"
                            value={input || ""}
                            onChange={e => setInput(e.target.value)}
                            data-testid={`deliver-input-${o.id}`}
                        />
                        <button onClick={() => onDeliver(o.id)} className="btn-saffron text-sm" data-testid={`deliver-btn-${o.id}`}>
                            <Send className="w-4 h-4" /> Send
                        </button>
                    </div>
                    {onMarkDelivered && (
                        <div className="flex justify-end">
                            <button
                                onClick={() => onMarkDelivered(o)}
                                className="text-xs text-muted2 hover:text-saffron-dark inline-flex items-center gap-1.5 underline-offset-4 hover:underline"
                                title="Move to Delivered without sending the email (use only if you've already shared the GPT outside the app)"
                                data-testid={`mark-delivered-${o.id}`}
                            >
                                <ArrowRight className="w-3.5 h-3.5" /> Already shared — move to Delivered
                            </button>
                        </div>
                    )}
                </div>
            )}

            {delivered && o.gpt_link && (
                <p className="mt-3 text-sm">
                    <a href={o.gpt_link} target="_blank" rel="noreferrer" className="text-saffron-dark underline break-all">{o.gpt_link}</a>
                </p>
            )}

            {delivered && onMoveToPending && (
                <div className="mt-3 flex justify-end">
                    <button
                        onClick={() => onMoveToPending(o)}
                        className="text-xs text-muted2 hover:text-saffron-dark inline-flex items-center gap-1.5 underline-offset-4 hover:underline"
                        title="Move this order back to Pending Delivery"
                        data-testid={`move-to-pending-${o.id}`}
                    >
                        <Undo2 className="w-3.5 h-3.5" /> Move back to Pending
                    </button>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, full }) {
    if (value == null || value === "") return null;
    if (Array.isArray(value)) {
        if (value.length === 0) return null;
        value = value.join(", ");
    }
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <span className="text-xs text-muted2 uppercase tracking-[0.18em] mr-2">{label}</span>
            <span className="text-ink">{value}</span>
        </div>
    );
}
